import {
  ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import * as R from 'remeda';

type RenderInlineOptions = {
  /** True when the item is rendered in the hidden measurement sandbox. */
  measurement?: boolean;
};

type OverflowContainerItem = {
  id: string;
  renderInline: (options?: RenderInlineOptions) => ReactNode;
  renderOverflow: (props: { close: () => void }) => ReactNode;
};

const computeFittingCount = (
  widths: ReadonlyArray<number>,
  budget: number,
  gap: number
): number => {
  let used = 0;

  for (let i = 0; i < widths.length; i += 1) {
    const width = widths[i] ?? 0;
    const next = used + width + (i > 0 ? gap : 0);

    if (next > budget + 0.5) {
      return i;
    }

    used = next;
  }

  return widths.length;
};

const computeVisibleCount = (
  widths: ReadonlyArray<number>,
  budget: number,
  gap: number,
  triggerWidth: number
): number => {
  const allFitCount = computeFittingCount(widths, budget, gap);

  if (allFitCount === widths.length) {
    return widths.length;
  }

  const budgetWithTrigger = Math.max(0, budget - triggerWidth - gap);
  return computeFittingCount(widths, budgetWithTrigger, gap);
};

type UseOverflowContainerOptions = {
  items: ReadonlyArray<OverflowContainerItem>;
  budget?: number;
  triggerWidth?: number;
  gap?: number;
};

type UseOverflowContainerResult = {
  rootRef: React.RefObject<HTMLDivElement | null>;
  itemRef: (id: string) => (el: HTMLElement | null) => void;
  visibleItems: ReadonlyArray<OverflowContainerItem>;
  overflowItems: ReadonlyArray<OverflowContainerItem>;
  ready: boolean;
};

const useOverflowContainer = ({
  items,
  budget,
  triggerWidth = 40,
  gap = 0,
}: UseOverflowContainerOptions): UseOverflowContainerResult => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const itemElsRef = useRef<Map<string, HTMLElement>>(new Map());
  const itemRefCacheRef = useRef<Map<string, (el: HTMLElement | null) => void>>(
    new Map()
  );

  const [selfBudget, setSelfBudget] = useState(0);
  const [visibleCount, setVisibleCount] = useState(items.length);

  // State (not derived from ref) so first mount triggers re-render once refs populate.
  const [measuredCount, setMeasuredCount] = useState(0);

  const resolvedBudget = budget ?? selfBudget;
  const ready = resolvedBudget > 0 && measuredCount === items.length;

  const itemRef = useCallback((id: string) => {
    const cached = itemRefCacheRef.current.get(id);

    if (cached) return cached;

    const ref = (el: HTMLElement | null) => {
      if (el) {
        itemElsRef.current.set(id, el);
      } else {
        itemElsRef.current.delete(id);
      }
    };

    itemRefCacheRef.current.set(id, ref);
    return ref;
  }, []);

  useLayoutEffect(() => {
    itemElsRef.current = new Map(
      R.pipe(
        items,
        R.map(item => [item.id, itemElsRef.current.get(item.id)] as const),
        R.filter(
          (entry): entry is readonly [string, HTMLElement] =>
            entry[1] instanceof HTMLElement
        )
      )
    );

    const currentIds = new Set(R.map(items, R.prop('id')));

    itemRefCacheRef.current.forEach((_, id) => {
      if (!currentIds.has(id)) {
        itemRefCacheRef.current.delete(id);
      }
    });

    const count = itemElsRef.current.size;
    setMeasuredCount(prev => (prev === count ? prev : count));
    setVisibleCount(items.length);
  }, [items]);

  useLayoutEffect(() => {
    if (budget !== undefined) {
      return undefined;
    }

    const root = rootRef.current;

    if (!root) {
      return undefined;
    }

    const observer = new ResizeObserver(entries => {
      const nextWidth = entries[0]?.contentRect.width ?? 0;
      setSelfBudget(prev =>
        Math.abs(prev - nextWidth) > 0.5 ? nextWidth : prev
      );
    });

    observer.observe(root);

    return () => observer.disconnect();
  }, [budget]);

  useLayoutEffect(() => {
    if (!ready) {
      return;
    }

    const widths = R.map(
      items,
      item =>
        itemElsRef.current.get(item.id)?.getBoundingClientRect().width ?? 0
    );

    const nextVisibleCount = computeVisibleCount(
      widths,
      resolvedBudget,
      gap,
      triggerWidth
    );

    setVisibleCount(prev =>
      prev === nextVisibleCount ? prev : nextVisibleCount
    );
  }, [gap, items, ready, resolvedBudget, triggerWidth]);

  return {
    rootRef,
    itemRef,
    visibleItems: ready ? R.take(items, visibleCount) : [],
    overflowItems: ready ? R.drop(items, visibleCount) : [],
    ready,
  };
};

export { useOverflowContainer };
export type {
  OverflowContainerItem,
  RenderInlineOptions,
  UseOverflowContainerOptions,
  UseOverflowContainerResult,
};
